import Controller from "csi/action/Controller";
import Request from "csi/def/Request";
import html from "csi/html";
import locale from "csi/locale";
import { routerStore } from "csi/state/rootStore";
import MessageDialog from "csi/widget/MessageDialog";
import xml from "csi/xml";
import { CallMethod } from "csi/xml/CallMethod";
import RadioButton from "dijit/form/RadioButton";
import declare from "dojo/_base/declare";
import lang from "dojo/_base/lang";

const FOOD_BOM = "FoodBOM";
const FOOD_BOM_CALCULATE_LABEL_INGREDIENTS = "FoodBOMCalculateLabelIngredients";
const FOOD_BOM_CALCULATE_LABEL_ALLERGENS = "FoodBOMCalculateLabelAllergens";
const FOOD_BOM_CALCULATE_LABEL_NUTRIENTS = "FoodBOMCalculateLabelNutrients";
const LABEL_CALCULATE_ITEMS_OPTION = "LabelItemsCalculateOption";
const LABEL_CALCULATE_ITEMS_OPTION_RESET = "Reset";
const LABEL_CALCULATE_ITEMS_OPTION_UPDATE = "Update";
const MODULE = "FormulatedProduct";
const RECIPE_CALCULATE_LABEL_ALLERGENS = "RecipeCalculateLabelAllergens";
const RECIPE_CALCULATE_LABEL_INGREDIENTS = "RecipeCalculateLabelIngredients";
const RECIPE_CALCULATE_LABEL_NUTRIENTS = "RecipeCalculateLabelNutrients";
const RECIPE_DATA_SHEET = "RecipeDataSheet";

const RecipeLabelItemToolbarAction = declare(Controller, {


	/**
	 * C8FB-786: Add Calculate Label Items action has form with Option as RadioButton: "Update" and "Reset"
	 * @param divMessage
	 */
	_createCalculateOptions(divMessage) {
		html.create("div", { class: "csi-form-radio-top" }, divMessage);

		// Add RadioButton "Update"
		const buttonUpdate = new RadioButton({
			id: LABEL_CALCULATE_ITEMS_OPTION_UPDATE,
			checked: true,
			value: LABEL_CALCULATE_ITEMS_OPTION_UPDATE,
			name: LABEL_CALCULATE_ITEMS_OPTION,
		});
		buttonUpdate.placeAt(divMessage);
		buttonUpdate.startup();

		html.create("label", { innerText: this.getString("_Update"), for: LABEL_CALCULATE_ITEMS_OPTION_UPDATE, class: "csi-form-radio-label csi-radio-button-group-item-label-bold csi-bold-key-label" }, divMessage);
		html.create("label", {
			innerText: this.getString("_DescriptionForUpdate"),
			class: "csi-form-radio-label-comment csi-radio-button-group-item-label csi-bold-key-label-value",
			for: LABEL_CALCULATE_ITEMS_OPTION_UPDATE,
		}, divMessage);
		html.create("div", { class: "csi-form-radio-gap" }, divMessage);

		// Add RadioButton "Reset"
		const buttonReset = new RadioButton({
			id: LABEL_CALCULATE_ITEMS_OPTION_RESET,
			value: LABEL_CALCULATE_ITEMS_OPTION_RESET,
			name: LABEL_CALCULATE_ITEMS_OPTION,
		});
		buttonReset.placeAt(divMessage);
		buttonReset.startup();
		html.create("label", { innerText: this.getString("_Reset"), for: LABEL_CALCULATE_ITEMS_OPTION_RESET, class: "csi-form-radio-label csi-radio-button-group-item-label-bold csi-bold-key-label" }, divMessage);
		html.create("label", {
			innerText: this.getString("_DescriptionForReset"),
			class: "csi-form-radio-label-comment csi-radio-button-group-item-label csi-bold-key-label-value",
			for: LABEL_CALCULATE_ITEMS_OPTION_RESET,
		}, divMessage);

		html.create("div", { class: "csi-form-radio-bottom" }, divMessage);
	},

	/**
	 * @override
	 * Displays a form with Options in radio buttons. Use "Run" as confirm button.
	 */
	confirmAction(params) {
		params = params || {};
		params.materialIcon = "";
		params.title = this.getString("_Options");
		params.onMessage = lang.hitch(this, this._createCalculateOptions);
		params.buttons = [MessageDialog.BUTTON_RUN + MessageDialog.BUTTON_PRIMARY, MessageDialog.BUTTON_CANCEL];

		this.inherited(arguments);
	},

	/**
	 * @override
	 * Use "Run" button as confirm button
	 */
	getConfirmButton() {
		return MessageDialog.BUTTON_RUN;
	},

	/**
	 * @override
	 * Add form to the action
	 */
	hasToConfirmAction() {
		return true;
	},

	/**
	 * @override
	 * Add Parameter URL with the value of Recipe Revision - Master
	 * Add Parameter String calculate option: "Update" or "Reset"
	 */
	toWriterRequest() {
		const call = this.inherited<CallMethod>(arguments);
		call.addParameter("URL", xml.ATTR_REF, routerStore.locationRevisionNode.$URL);
		call.addParameter("Option", xml.ATTR_STRING, this.confirmValues[LABEL_CALCULATE_ITEMS_OPTION]);
		return call;
	},

	/**
	 * @override
	 * Determine which operation to send to send request to depending
	 * on if parent DataSheet of FoodLabelDataSheet is FoodBOM or RecipeDataSheet
	 */
	toWriter() {
		const action = this.getAction();
		const cache = this.getCache();
		const foodLabelParentNode = cache.getPathNode(this.getParent(), "Child:__Parent__/Child:__Parent__");
		if (!foodLabelParentNode) {
			// Throw error nessage if calculate option is performed not on a FoodLabelDataSheet Revision
			throw new Error("Cannot Perform Calculations Without a " + locale.type("FoodLabelDataSheet"));
		}

		let request;
		const calculateActionId = action.getId();
		if (foodLabelParentNode.$Type === RECIPE_DATA_SHEET) {
			switch (calculateActionId) {
				case "CalculateAllergens":
					request = lang.mixin(new Request(), {
						m_module: MODULE,
						m_operation: RECIPE_CALCULATE_LABEL_ALLERGENS,
					});
					break;
				case "CalculateIngredients":
					request = lang.mixin(new Request(), {
						m_module: MODULE,
						m_operation: RECIPE_CALCULATE_LABEL_INGREDIENTS,
					});
					break;
				case "CalculateNutrients":
					request = lang.mixin(new Request(), {
						m_module: MODULE,
						m_operation: RECIPE_CALCULATE_LABEL_NUTRIENTS,
					});
					break;
			}
		} else if (foodLabelParentNode.$Type === FOOD_BOM) {
			switch (calculateActionId) {
				case "CalculateAllergens":
					request = lang.mixin(new Request(), {
						m_module: MODULE,
						m_operation: FOOD_BOM_CALCULATE_LABEL_ALLERGENS,
					});
					break;
				case "CalculateIngredients":
					request = lang.mixin(new Request(), {
						m_module: MODULE,
						m_operation: FOOD_BOM_CALCULATE_LABEL_INGREDIENTS,
					});
					break;
				case "CalculateNutrients":
					request = lang.mixin(new Request(), {
						m_module: MODULE,
						m_operation: FOOD_BOM_CALCULATE_LABEL_NUTRIENTS,
					});
					break;
			}
		}

		action.m_request = request;
		return request ? this.toWriterRequest() : null;
	},

});

export = RecipeLabelItemToolbarAction;